import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Search, X } from 'lucide-react';
import BlogContentActionModal from './BlogContentActionModal';
import BlogCreatorWorkflowDetails from './BlogCreatorWorkflowDetails';
import { TopicGroup } from '../data/topicalContentData';
import { useBrand } from '../context/BrandContext';
import { getBrandStorageItem } from '../utils/brandStorage';

type TopicStatus = 'pending' | 'running' | 'completed' | 'error';

interface TopicRow {
  id: string;
  topicTitle: string;
  primaryKeyword: string;
  secondaryKeywords: string[];
  groupId: string;
  status: TopicStatus;
  progress: string;
  output: string;
  metadata?: {
    title: string;
    description: string;
    metaSeoTitle?: string;
    metaDescription?: string;
    word_count: number;
    seo_optimized: boolean;
    citations_included: boolean;
    brand_variables_processed: number;
    aeo_optimized: boolean;
  };
  generationResult?: any;
  workflowDetails?: {
    firecrawl?: any;
    deepResearch?: any;
    gapAnalysis?: any;
    contentGeneration?: any;
    currentStage?: string;
    retryCount?: number;
    canResume?: boolean;
  };
}

interface TopicsAgentPageProps {
  pageTitle: string;
  pageDescription: string;
  storageKeyPrefix: string;
  generateAsyncUrl: string;
  getJobStatusUrl: (jobId: string) => string;
  staticGroups?: TopicGroup[];
  loadGroups?: () => Promise<TopicGroup[]>;
  clusterKeyFromGroupName?: (groupName: string) => string | undefined;
  clusterProductHints?: Record<string, string>;
}

const extractWorkflowDetailsFromResult = (result: any): TopicRow['workflowDetails'] => {
  if (!result?.workflow_data) return undefined;

  const { workflow_data } = result;
  const workflowDetails: TopicRow['workflowDetails'] = { currentStage: 'completed' };

  if (workflow_data.firecrawl_analysis) {
    const firecrawl = workflow_data.firecrawl_analysis;
    workflowDetails.firecrawl = {
      urls_analyzed: firecrawl.top_results?.map((row: any) => row.url) || [],
      competitor_titles: firecrawl.top_results?.map((row: any) => row.title) || [],
      key_topics: firecrawl.top_results?.flatMap((row: any) => row.key_topics || []) || [],
      content_structure_insights: firecrawl.top_results?.map((row: any) => `${row.word_count || 0} words`) || [],
      search_metadata: firecrawl.search_metadata,
    };
  }

  const deepResearch = workflow_data.web_research || workflow_data.deep_research;
  if (deepResearch) {
    workflowDetails.deepResearch = {
      key_insights: deepResearch.research_findings?.key_insights || [],
      market_trends: deepResearch.research_findings?.market_trends || [],
      audience_needs: deepResearch.research_findings?.audience_needs || [],
      content_gaps: deepResearch.research_findings?.content_gaps || [],
      research_confidence: deepResearch.research_depth?.confidence_score || 0,
      sources_analyzed: deepResearch.research_depth?.sources_analyzed || 0,
      model_used: deepResearch.research_metadata?.model_used || 'Unknown',
    };
  }

  if (workflow_data.gap_analysis) {
    const gapAnalysis = workflow_data.gap_analysis;
    workflowDetails.gapAnalysis = {
      identified_gaps: gapAnalysis.analysis_summary?.identified_gaps || [],
      competitive_coverage: gapAnalysis.analysis_summary?.competitive_coverage || '',
      recommended_angle: gapAnalysis.analysis_summary?.recommended_content_angle || '',
      gap_scores: gapAnalysis.gap_score || {},
      seo_suggestions: gapAnalysis.content_strategy?.seo_optimization_suggestions || [],
    };
  }

  if (result.generation_metadata) {
    workflowDetails.contentGeneration = {
      processing_steps: result.generation_metadata.processing_steps || [],
      brand_variables_processed: result.metadata?.brand_variables_processed || 0,
      citations_count: result.metadata?.citations_included ? 1 : 0,
      quality_score: result.generation_metadata.content_quality_score || 0,
      model_pipeline: result.generation_metadata.model_pipeline || [],
    };
  }

  return workflowDetails;
};

const buildRowsFromGroups = (groups: TopicGroup[]): TopicRow[] =>
  groups.flatMap((group) =>
    group.topics.map((topic) => ({
      id: topic.id,
      topicTitle: topic.title,
      primaryKeyword: topic.primaryKeyword,
      secondaryKeywords: topic.secondaryKeywords,
      groupId: group.id,
      status: 'pending' as const,
      progress: '',
      output: '',
    }))
  );

const TopicsAgentPage: React.FC<TopicsAgentPageProps> = ({
  pageTitle,
  pageDescription,
  storageKeyPrefix,
  generateAsyncUrl,
  getJobStatusUrl,
  staticGroups,
  loadGroups,
  clusterKeyFromGroupName,
  clusterProductHints,
}) => {
  const { brandKey } = useBrand();
  const rowsStorageKey = `${storageKeyPrefix}_rows`;
  const outputKeyPrefix = `${storageKeyPrefix}_output_`;

  // Derive the Dr. Mani product key from the page prefix so the action modal can
  // generate product-aware GPT-Image-2 images (PSB/CKAA/SS) instead of tree photos.
  const productImageKey: 'psb' | 'ckaa' | 'ss' | undefined =
    storageKeyPrefix.startsWith('ckaa')
      ? 'ckaa'
      : storageKeyPrefix.startsWith('psb')
      ? 'psb'
      : storageKeyPrefix.startsWith('ss')
      ? 'ss'
      : undefined;

  const [groups, setGroups] = useState<TopicGroup[]>(staticGroups || []);
  const [isLoadingGroups, setIsLoadingGroups] = useState(!staticGroups && !!loadGroups);
  const [groupLoadError, setGroupLoadError] = useState('');
  const [rows, setRows] = useState<TopicRow[]>([]);
  const [storageWarning, setStorageWarning] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [showContentActionModal, setShowContentActionModal] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState<TopicRow | null>(null);
  const [popupState, setPopupState] = useState<{ isVisible: boolean; topicId: string | null; position: { x: number; y: number } }>({
    isVisible: false,
    topicId: null,
    position: { x: 0, y: 0 },
  });
  const [hideTimeout, setHideTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isSequentialRunning, setIsSequentialRunning] = useState(false);
  const [sequentialRemaining, setSequentialRemaining] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(200);

  const stopRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const rowsRef = useRef(rows);
  const groupsRef = useRef(groups);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    groupsRef.current = groups;
  }, [groups]);

  useEffect(() => {
    if (staticGroups || !loadGroups) {
      return;
    }

    let isMounted = true;

    const load = async () => {
      try {
        setIsLoadingGroups(true);
        const loadedGroups = await loadGroups();
        if (!isMounted) return;
        setGroups(loadedGroups);
        setExpandedGroups(new Set(loadedGroups.map((group) => group.id)));
      } catch (error) {
        if (!isMounted) return;
        setGroupLoadError(error instanceof Error ? error.message : 'Failed to load topic groups');
      } finally {
        if (isMounted) {
          setIsLoadingGroups(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [loadGroups, staticGroups]);

  useEffect(() => {
    if (staticGroups) {
      setExpandedGroups(new Set(staticGroups.map((group) => group.id)));
    }
  }, [staticGroups]);

  useEffect(() => {
    if (groups.length === 0) {
      setRows([]);
      return;
    }

    const defaultRows = buildRowsFromGroups(groups);

    try {
      const saved = localStorage.getItem(rowsStorageKey);
      if (saved) {
        const parsed: TopicRow[] = JSON.parse(saved);
        const savedMap = new Map(parsed.map((row) => [row.id, row]));

        setRows(
          defaultRows.map((row) => {
            const savedRow = savedMap.get(row.id);
            if (!savedRow) {
              return row;
            }

            let output = savedRow.output || '';
            if (!output && savedRow.status === 'completed') {
              try {
                output = localStorage.getItem(`${outputKeyPrefix}${row.id}`) || '';
              } catch {
                output = '';
              }
            }

            return {
              ...row,
              ...savedRow,
              output,
              status: savedRow.status === 'running' ? 'error' : savedRow.status,
              progress: savedRow.status === 'running' ? 'Interrupted by page reload' : savedRow.progress,
            };
          })
        );
        return;
      }
    } catch {
      setStorageWarning(true);
    }

    setRows(defaultRows);
  }, [groups, outputKeyPrefix, rowsStorageKey]);

  useEffect(() => {
    if (rows.length === 0) {
      return;
    }

    try {
      const metadata = rows.map(({ output, ...rest }) => rest);
      localStorage.setItem(rowsStorageKey, JSON.stringify(metadata));

      for (const row of rows) {
        if (!row.output) {
          continue;
        }

        try {
          localStorage.setItem(`${outputKeyPrefix}${row.id}`, row.output);
        } catch {
          setStorageWarning(true);
          break;
        }
      }
    } catch {
      setStorageWarning(true);
    }
  }, [outputKeyPrefix, rows, rowsStorageKey]);

  useEffect(
    () => () => {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
      }
    },
    [hideTimeout]
  );

  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) {
      return rows;
    }

    const normalizedSearchTerm = searchTerm.toLowerCase();
    return rows.filter(
      (row) =>
        row.topicTitle.toLowerCase().includes(normalizedSearchTerm) ||
        row.primaryKeyword.toLowerCase().includes(normalizedSearchTerm)
    );
  }, [rows, searchTerm]);

  const filteredGroups = useMemo(
    () =>
      groups
        .map((group) => ({
          ...group,
          topics: group.topics.filter((topic) => filteredRows.some((row) => row.id === topic.id)),
        }))
        .filter((group) => group.topics.length > 0),
    [filteredRows, groups]
  );

  const totalTopics = useMemo(() => groups.reduce((sum, group) => sum + group.topics.length, 0), [groups]);
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const anyRunning = rows.some((row) => row.status === 'running');

  const allFilteredTopicIds = filteredGroups.flatMap((group) => group.topics.map((topic) => topic.id));
  const pageStart = (page - 1) * pageSize;
  const pageTopicIds = new Set(allFilteredTopicIds.slice(pageStart, pageStart + pageSize));
  const paginatedGroups = filteredGroups
    .map((group) => ({ ...group, topics: group.topics.filter((topic) => pageTopicIds.has(topic.id)) }))
    .filter((group) => group.topics.length > 0);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const toggleRowSelection = (rowId: string) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const selectAll = () => setSelectedRows(new Set(filteredRows.map((row) => row.id)));
  const clearSelection = () => setSelectedRows(new Set());

  const updateRow = useCallback((rowId: string, updates: Partial<TopicRow>) => {
    setRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, ...updates } : row)));
  }, []);

  const loadSitemapData = useCallback((): Array<{ title: string; description: string; url: string }> | null => {
    try {
      const stored = getBrandStorageItem(brandKey, 'sitemap_data', {
        legacyKeys: ['us_citrus_sitemap_data', 'us_citrus_ai_sitemap_data'],
      });

      if (!stored) {
        return null;
      }

      const sitemaps = JSON.parse(stored);
      const allUrls = sitemaps.flatMap((sitemap: any) =>
        sitemap.urls.map((url: any) => ({
          title: url.title,
          description: url.description,
          url: url.url,
        }))
      );

      return allUrls.slice(0, 30);
    } catch {
      return null;
    }
  }, [brandKey]);

  const pollJob = useCallback(
    async (topicId: string, jobId: string, signal?: AbortSignal) => {
      const maxPolls = 180;
      let polls = 0;
      const interval = 5000;

      const poll = async () => {
        if (signal?.aborted) {
          updateRow(topicId, { status: 'error', progress: 'Cancelled by user' });
          return;
        }

        if (polls++ >= maxPolls) {
          updateRow(topicId, { status: 'error', progress: 'Timed out after 15 minutes' });
          return;
        }

        try {
          const response = await fetch(getJobStatusUrl(jobId), {
            headers: { 'x-api-key': process.env.REACT_APP_INTERNAL_API_KEY || '' },
            ...(signal ? { signal } : {}),
          });
          const payload = await response.json();

          if (!payload.success) {
            updateRow(topicId, { status: 'error', progress: payload.error || 'Unknown error' });
            return;
          }

          const { data } = payload;
          updateRow(topicId, { progress: data.message || `Stage: ${data.stage}` });

          if (data.status === 'completed' && data.result) {
            const result = data.result;
            const workflowDetails = extractWorkflowDetailsFromResult(result);

            updateRow(topicId, {
              status: 'completed',
              progress: 'Complete!',
              output: result.content || result.raw_content || '',
              ...(result.metadata ? { metadata: result.metadata } : {}),
              generationResult: result,
              ...(workflowDetails ? { workflowDetails } : {}),
            });
            return;
          }

          if (data.status === 'error') {
            updateRow(topicId, { status: 'error', progress: data.error || 'Generation failed' });
            return;
          }

          setTimeout(poll, interval);
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') {
            updateRow(topicId, { status: 'error', progress: 'Cancelled by user' });
            return;
          }

          updateRow(topicId, {
            status: 'error',
            progress: error instanceof Error ? error.message : 'Polling failed',
          });
        }
      };

      poll();
    },
    [getJobStatusUrl, updateRow]
  );

  const executeTopic = useCallback(
    async (topicId: string) => {
      const row = rowsRef.current.find((item) => item.id === topicId);
      if (!row) {
        return;
      }

      const controller = new AbortController();
      abortRef.current = controller;

      updateRow(topicId, { status: 'running', progress: 'Starting...', output: '' });

      try {
        const sitemapData = loadSitemapData();
        const topicRowIndex = rowsRef.current.findIndex((item) => item.id === topicId);
        const authorKey = topicRowIndex >= 0 && topicRowIndex % 2 === 0 ? 'dr_mani' : 'ron_skaria';
        const group = groupsRef.current.find((item) => item.id === row.groupId);
        const topicClusterKey =
          clusterKeyFromGroupName && group ? clusterKeyFromGroupName(group.name) : undefined;
        const response = await fetch(generateAsyncUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_INTERNAL_API_KEY || '',
          },
          body: JSON.stringify({
            keyword: row.primaryKeyword,
            topicTitle: row.topicTitle,
            secondaryKeywords: row.secondaryKeywords,
            content_length: 'long',
            use_default_prompts: true,
            authorKey,
            topicRowIndex: topicRowIndex >= 0 ? topicRowIndex : 0,
            ...(topicClusterKey ? { topicClusterKey } : {}),
            ...(sitemapData ? { sitemap_data: sitemapData } : {}),
          }),
          signal: controller.signal,
        });
        const payload = await response.json();

        if (!payload.success || !payload.jobId) {
          updateRow(topicId, { status: 'error', progress: payload.error || 'Failed to start' });
          return;
        }

        updateRow(topicId, { progress: 'Job queued...' });
        await pollJob(topicId, payload.jobId, controller.signal);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          updateRow(topicId, { status: 'error', progress: 'Cancelled by user' });
          return;
        }

        updateRow(topicId, {
          status: 'error',
          progress: error instanceof Error ? error.message : 'Network error',
        });
      }
    },
    [generateAsyncUrl, loadSitemapData, pollJob, updateRow, clusterKeyFromGroupName]
  );

  const executeSelected = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) {
        return;
      }

      stopRef.current = false;
      setIsSequentialRunning(true);
      setSequentialRemaining([...ids]);

      for (const id of ids) {
        if (stopRef.current) {
          break;
        }

        setSequentialRemaining((prev) => prev.filter((pendingId) => pendingId !== id));
        await executeTopic(id);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      setIsSequentialRunning(false);
      setSequentialRemaining([]);
    },
    [executeTopic]
  );

  const retryTopic = useCallback(
    (id: string) => {
      updateRow(id, { status: 'pending', progress: '', output: '' });
      void executeTopic(id);
    },
    [executeTopic, updateRow]
  );

  const openOutputModal = (id: string) => {
    const row = rows.find((item) => item.id === id);
    if (row?.output) {
      setSelectedTopic(row);
      setShowContentActionModal(true);
    }
  };

  const showBackendPopup = (id: string, event: React.MouseEvent) => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
    }

    setPopupState({
      isVisible: true,
      topicId: id,
      position: { x: event.clientX, y: event.clientY },
    });
  };

  return (
    <>
      <style>{'@keyframes pulse{0%{opacity:1}50%{opacity:.5}100%{opacity:1}}'}</style>
      <div style={{ minHeight: '100vh', backgroundColor: '#ffffff', padding: '2rem' }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 600, color: '#111827', marginBottom: '0.5rem' }}>{pageTitle}</h1>
          <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>{pageDescription}</p>
        </div>

        {storageWarning && (
          <div
            style={{
              marginBottom: '1rem',
              padding: '0.75rem 1rem',
              background: '#FEF3C7',
              border: '1px solid #F59E0B',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              color: '#92400E',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>Browser storage is full, completed outputs may not persist across reloads. Consider exporting your content.</span>
            <button
              onClick={() => setStorageWarning(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400E', fontWeight: 600 }}
            >
              Dismiss
            </button>
          </div>
        )}

        {groupLoadError && (
          <div
            style={{
              marginBottom: '1rem',
              padding: '0.75rem 1rem',
              background: '#FEF2F2',
              border: '1px solid #FCA5A5',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              color: '#991B1B',
            }}
          >
            {groupLoadError}
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 250px', maxWidth: '400px' }}>
            <Search
              size={16}
              style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}
            />
            <input
              type="text"
              placeholder="Search topics or keywords..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              style={{
                width: '100%',
                padding: '0.4rem 0.75rem 0.4rem 2.25rem',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: '0.5rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9ca3af',
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>

          <span style={{ fontSize: '0.875rem', color: '#3AB981', fontWeight: 500 }}>
            {isLoadingGroups ? 'Loading topics...' : `${totalTopics} topics loaded`}
          </span>

          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              onClick={() => (selectedRows.size > 0 ? clearSelection() : selectAll())}
              disabled={isLoadingGroups || filteredRows.length === 0}
              style={{
                padding: '0.4rem 0.75rem',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem',
                background: '#fff',
                cursor: isLoadingGroups || filteredRows.length === 0 ? 'not-allowed' : 'pointer',
                opacity: isLoadingGroups || filteredRows.length === 0 ? 0.6 : 1,
              }}
            >
              {selectedRows.size > 0 ? `Unselect (${selectedRows.size})` : 'Select All'}
            </button>

            <button
              onClick={() => void executeSelected(Array.from(selectedRows))}
              disabled={isSequentialRunning || selectedRows.size === 0 || isLoadingGroups}
              style={{
                padding: '0.4rem 0.75rem',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem',
                background: isSequentialRunning ? '#e5e7eb' : '#fff',
                cursor: isSequentialRunning || selectedRows.size === 0 || isLoadingGroups ? 'not-allowed' : 'pointer',
              }}
            >
              Run Selected ({selectedRows.size})
            </button>

            {isSequentialRunning && (
              <button
                onClick={() => {
                  stopRef.current = true;
                  abortRef.current?.abort();
                }}
                style={{
                  padding: '0.4rem 0.75rem',
                  border: '1px solid #ee4444',
                  borderRadius: '0.5rem',
                  background: '#ee4444',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                Stop
              </button>
            )}

            {!isSequentialRunning && sequentialRemaining.length > 0 && (
              <button
                onClick={() => void executeSelected(sequentialRemaining)}
                style={{
                  padding: '0.4rem 0.75rem',
                  border: '1px solid #3AB981',
                  borderRadius: '0.5rem',
                  background: '#3AB981',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                Resume ({sequentialRemaining.length})
              </button>
            )}
          </div>
        </div>

        <div style={{ backgroundColor: '#ffffff', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
          <div style={{ maxHeight: '65vh', overflow: 'auto' }}>
            <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0 }}>
              <colgroup>
                <col style={{ width: '3%' }} />
                <col style={{ width: '45%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '14%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={stickyHeaderStyle}>
                    <input
                      type="checkbox"
                      checked={filteredRows.length > 0 && selectedRows.size === filteredRows.length}
                      onChange={() => (selectedRows.size === filteredRows.length ? clearSelection() : selectAll())}
                      style={{ cursor: 'pointer', marginTop: '4px' }}
                    />
                  </th>
                  <th style={stickyHeaderStyle}>TOPIC / KEYWORD</th>
                  <th style={stickyHeaderStyle}>STATUS</th>
                  <th style={stickyHeaderStyle}>EXECUTE</th>
                  <th style={stickyHeaderStyle}>DETAILS</th>
                  <th style={stickyHeaderStyle}>ACTIONS</th>
                </tr>
              </thead>
              <tbody style={{ fontSize: '0.875rem' }}>
                {isLoadingGroups ? (
                  <tr>
                    <td colSpan={6} style={{ padding: '3rem 1.5rem', textAlign: 'center', color: '#6b7280' }}>
                      Loading grouped topics...
                    </td>
                  </tr>
                ) : paginatedGroups.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: '3rem 1.5rem', textAlign: 'center', color: '#6b7280' }}>
                      {searchTerm ? 'No topics match your search' : 'No topics available'}
                    </td>
                  </tr>
                ) : (
                  paginatedGroups.map((group) => {
                    const isExpanded = expandedGroups.has(group.id);
                    const groupRows = rows.filter(
                      (row) => row.groupId === group.id && filteredRows.some((filteredRow) => filteredRow.id === row.id)
                    );
                    const completedCount = groupRows.filter((row) => row.status === 'completed').length;
                    const runningCount = groupRows.filter((row) => row.status === 'running').length;
                    const clusterKey = clusterKeyFromGroupName?.(group.name);
                    const clusterProductHint = clusterKey && clusterProductHints ? clusterProductHints[clusterKey] : undefined;

                    return (
                      <React.Fragment key={group.id}>
                        <tr style={{ cursor: 'pointer', backgroundColor: '#f9fafb' }} onClick={() => toggleGroup(group.id)}>
                          <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>
                            {isExpanded ? (
                              <ChevronDown size={16} style={{ color: '#6b7280' }} />
                            ) : (
                              <ChevronRight size={16} style={{ color: '#6b7280' }} />
                            )}
                          </td>
                          <td colSpan={5} style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span
                                style={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  backgroundColor: group.color,
                                  display: 'inline-block',
                                  flexShrink: 0,
                                }}
                              />
                              <span style={{ fontWeight: 600, color: '#111827' }}>{group.name}</span>
                              {clusterProductHint && (
                                <span style={{ fontSize: '0.75rem', color: '#4b5563', marginLeft: '0.25rem' }}>
                                  → {clusterProductHint}
                                </span>
                              )}
                              <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.25rem' }}>
                                {group.topics.length} topics
                                {completedCount > 0 && <span style={{ color: '#10b981', marginLeft: '0.5rem' }}>{completedCount} done</span>}
                                {runningCount > 0 && <span style={{ color: '#3b82f6', marginLeft: '0.5rem' }}>{runningCount} running</span>}
                              </span>
                            </div>
                          </td>
                        </tr>

                        {isExpanded &&
                          groupRows.map((row) => (
                            <tr
                              key={row.id}
                              style={{
                                backgroundColor: row.status === 'completed' ? '#f0fdf4' : 'transparent',
                                opacity: row.status === 'completed' ? 0.7 : 1,
                              }}
                            >
                              <td style={{ padding: '0.75rem 0.75rem 0.75rem 2rem', borderBottom: '1px solid #f3f4f6' }}>
                                <input
                                  type="checkbox"
                                  checked={selectedRows.has(row.id)}
                                  onChange={() => toggleRowSelection(row.id)}
                                  onClick={(event) => event.stopPropagation()}
                                  style={{ cursor: 'pointer', marginTop: '4px' }}
                                />
                              </td>

                              <td style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                                <div
                                  style={{
                                    fontWeight: 500,
                                    color: '#111827',
                                    marginBottom: '0.25rem',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                  title={row.topicTitle}
                                >
                                  {row.topicTitle}
                                </div>
                                {row.primaryKeyword !== row.topicTitle && (
                                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{row.primaryKeyword}</div>
                                )}
                                {row.progress && row.status !== 'pending' && (
                                  <div
                                    style={{
                                      fontSize: '0.75rem',
                                      color: row.status === 'error' ? '#dc2626' : '#6b7280',
                                      marginTop: '0.25rem',
                                    }}
                                  >
                                    {row.progress}
                                  </div>
                                )}
                              </td>

                              <td style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                                {renderStatusLabel(row.status)}
                              </td>

                              <td style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                                {row.status === 'pending' ? (
                                  <button
                                    onClick={() => void executeTopic(row.id)}
                                    disabled={anyRunning}
                                    style={getButtonStyle(anyRunning, '#fff', '#111827')}
                                  >
                                    Run
                                  </button>
                                ) : row.status === 'running' ? (
                                  <button disabled style={getButtonStyle(true, '#e5e7eb', '#111827')} title={row.progress}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <span
                                        style={{
                                          display: 'inline-block',
                                          width: 8,
                                          height: 8,
                                          borderRadius: '50%',
                                          backgroundColor: '#10b981',
                                          animation: 'pulse 1.5s ease-in-out infinite',
                                        }}
                                      />
                                      Running
                                    </span>
                                  </button>
                                ) : row.status === 'completed' ? (
                                  <button
                                    onClick={() => retryTopic(row.id)}
                                    disabled={anyRunning}
                                    style={getButtonStyle(anyRunning, '#fff', '#111827')}
                                  >
                                    Re-run
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => retryTopic(row.id)}
                                    disabled={anyRunning}
                                    style={getButtonStyle(anyRunning, '#fef2f2', '#dc2626')}
                                  >
                                    Retry
                                  </button>
                                )}
                              </td>

                              <td style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                                <button
                                  onClick={(event) => showBackendPopup(row.id, event)}
                                  disabled={!row.output || row.status === 'running'}
                                  style={getButtonStyle(!(row.output && row.status !== 'running'), '#fff', '#111827')}
                                >
                                  Show
                                </button>
                              </td>

                              <td style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                                <button
                                  onClick={() => openOutputModal(row.id)}
                                  disabled={!row.output || row.status === 'running'}
                                  style={getButtonStyle(!(row.output && row.status !== 'running'), '#EBF212', '#000')}
                                >
                                  See Output
                                </button>
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div
            style={{
              padding: '0.75rem 1rem',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '0.875rem',
              color: '#6b7280',
            }}
          >
            <div>
              Page {page} of {totalPages} • Showing {filteredRows.length} of {totalTopics} topics
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button onClick={() => setPage(1)} disabled={page === 1} style={pagerButtonStyle(page === 1)}>
                {'<<'}
              </button>
              <button onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={page === 1} style={pagerButtonStyle(page === 1)}>
                {'<'}
              </button>
              <button
                onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                disabled={page === totalPages}
                style={pagerButtonStyle(page === totalPages)}
              >
                {'>'}
              </button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={pagerButtonStyle(page === totalPages)}>
                {'>>'}
              </button>
              <label style={{ color: '#374151', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                Rows per page
                <select
                  value={pageSize}
                  onChange={(event) => {
                    setPageSize(Number(event.target.value));
                    setPage(1);
                  }}
                  style={{ padding: '0.3rem 0.6rem', border: '1px solid #e5e7eb', borderRadius: '0.375rem', background: '#fff' }}
                >
                  {[50, 100, 200, 500].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        </div>

        {popupState.isVisible &&
          popupState.topicId &&
          (() => {
            const row = rows.find((item) => item.id === popupState.topicId);
            if (!row?.workflowDetails) {
              return null;
            }

            return (
              <BlogCreatorWorkflowDetails
                workflowDetails={row.workflowDetails}
                status={row.status}
                isVisible={popupState.isVisible}
                keyword={row.primaryKeyword}
                position={popupState.position}
                onMobileClose={() => setPopupState((prev) => ({ ...prev, isVisible: false }))}
                onMouseEnter={() => {
                  if (hideTimeout) {
                    clearTimeout(hideTimeout);
                  }
                }}
                onMouseLeave={() => {
                  const timeout = setTimeout(() => setPopupState((prev) => ({ ...prev, isVisible: false })), 300);
                  setHideTimeout(timeout);
                }}
              />
            );
          })()}

        {showContentActionModal && selectedTopic && (
          <BlogContentActionModal
            isOpen={showContentActionModal}
            onClose={() => {
              setShowContentActionModal(false);
              setSelectedTopic(null);
            }}
            keywordRow={{
              id: selectedTopic.id,
              keyword: selectedTopic.primaryKeyword,
              status: selectedTopic.status,
              progress: selectedTopic.progress,
              output: selectedTopic.output,
              createdAt: new Date(),
              ...(selectedTopic.metadata ? { metadata: selectedTopic.metadata } : {}),
              ...(selectedTopic.generationResult ? { generationResult: selectedTopic.generationResult } : {}),
            }}
            {...(productImageKey ? { productImageKey } : {})}
          />
        )}
      </div>
    </>
  );
};

const stickyHeaderStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  background: '#f9fafb',
  zIndex: 2,
  textAlign: 'left',
  padding: '0.75rem',
  fontSize: '0.75rem',
  color: '#6b7280',
  borderBottom: '1px solid #e5e7eb',
};

const renderStatusLabel = (status: TopicStatus) => {
  if (status === 'pending') return <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Pending</span>;
  if (status === 'running') {
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#3b82f6', fontSize: '0.75rem' }}>
        <span
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: '#10b981',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
        Running
      </span>
    );
  }
  if (status === 'completed') return <span style={{ color: '#10b981', fontSize: '0.75rem' }}>Done</span>;
  return <span style={{ color: '#ef4444', fontSize: '0.75rem' }}>Error</span>;
};

const getButtonStyle = (disabled: boolean, activeBackground: string, activeColor: string): React.CSSProperties => ({
  padding: '0.3rem 0.6rem',
  borderRadius: '0.5rem',
  border: '1px solid #e5e7eb',
  background: disabled ? '#e5e7eb' : activeBackground,
  color: disabled ? '#9ca3af' : activeColor,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const pagerButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding: '0.3rem 0.6rem',
  border: '1px solid #e5e7eb',
  borderRadius: '0.375rem',
  background: disabled ? '#e5e7eb' : '#fff',
  cursor: disabled ? 'not-allowed' : 'pointer',
});

export default TopicsAgentPage;
